import { Resume } from 'packages/types'
import { useI18n } from 'builder/hooks/useI18n'
import { TooltipV2 } from 'builder/components/Tooltip'
import { LayoutFormatChangeHandler, SettingsFieldChangeHandler } from 'builder/modules/resumeEditor'
import { DEFAULT_TEMPLATE_SETTINGS } from 'builder/modules/resumeEditor/constants'
import {
  SelectContainer,
  SelectLabel,
  CustomSelect,
  SliderLabel,
  SliderContainer,
  TabContentContainer,
  GroupControl,
} from '../../styles'
import { useTemplateCustomization } from '../../hooks/useTemplateCustomization'
import { GenericAbsoluteSlider } from '../CustomizationSlider/GenericAbsoluteSlider'
import { usePageFormatCustomisation } from '../../hooks/usePageFormatCustomisation'
import { useDateFormatCustomisation } from '../../hooks/useDateFormatCustomisation'
import { HeaderAlignmentSelection } from './HeaderAlignmentSelection'
import { DateAlignmentSelection } from './DateAlignmentSelection'
import { LocationAlignmentSelection } from './LocationAlignmentSelection'
import { EducationBlockOrderSelection } from './EducationBlockOrderSelection'
import { EducationBlockLayoutSelection } from './EducationBlockLayoutSelection'
import { SkillBlockLayoutSelection } from './SkillBlockLayoutSelection'

const TRANSLATION = 'builder.resume_editor.document_customization.sidebar.layout_tab'

type Props = {
  resume: Resume
  handleChangeLayoutFormat?: LayoutFormatChangeHandler
  handleSettingsFieldChange?: SettingsFieldChangeHandler
}

export const LayoutCustomization = (props: Props) => {
  const { resume, handleChangeLayoutFormat, handleSettingsFieldChange } = props
  const { i18n } = useI18n()

  const pageFormat = usePageFormatCustomisation(resume, handleChangeLayoutFormat)
  const dateFormat = useDateFormatCustomisation(resume, handleSettingsFieldChange)

  const { supportsLayoutFormats, resumeSpecificTemplateSettings, currentTemplate } =
    useTemplateCustomization(resume)

  const {
    verticalPageMargins = currentTemplate?.settings.verticalPageMargins?.default ||
      DEFAULT_TEMPLATE_SETTINGS.verticalPageMargins,
    horizontalPageMargins = currentTemplate?.settings.horizontalPageMargins?.default ||
      DEFAULT_TEMPLATE_SETTINGS.horizontalPageMargins,
    marginsBetweenSections = currentTemplate?.settings.marginsBetweenSections?.default ||
      DEFAULT_TEMPLATE_SETTINGS.marginsBetweenSections,
    marginsBetweenSectionNameAndContent = currentTemplate?.settings
      .marginsBetweenSectionNameAndContent?.default ||
      DEFAULT_TEMPLATE_SETTINGS.marginsBetweenSectionNameAndContent,
    marginsBetweenCards = currentTemplate?.settings.marginsBetweenCards?.default ||
      DEFAULT_TEMPLATE_SETTINGS.marginsBetweenCards,
  } = resumeSpecificTemplateSettings || {}

  return (
    <TabContentContainer>
      <TooltipV2
        value={i18n.t(`${TRANSLATION}.format_customization_warning`)}
        width={220}
        multiline={true}
        position="bottom"
        isActive={!supportsLayoutFormats}
      >
        <SelectContainer>
          <SelectLabel>{i18n.t(`${TRANSLATION}.format`)}</SelectLabel>
          <CustomSelect
            selected={pageFormat.selectedLayoutFormatOption}
            onSelect={pageFormat.onSelect}
            options={pageFormat.letterFormatOptions}
            optionsListClassname="paper-format"
            buttonOnClick={pageFormat.onButtonClick}
            disabled={!pageFormat.supportsLayoutFormats}
            useNativeSelectOnMobile={false}
          />
        </SelectContainer>
      </TooltipV2>

      <GroupControl title={i18n.t(`${TRANSLATION}.margin_and_paddings`)} spacing="L">
        <SliderContainer>
          <SliderLabel>{i18n.t(`${TRANSLATION}.vertical_page_margins`)}</SliderLabel>
          <GenericAbsoluteSlider
            value={verticalPageMargins}
            disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
            isDisabled={!currentTemplate?.supportsVerticalPageMargins || !handleSettingsFieldChange}
            onChange={newValue => {
              handleSettingsFieldChange?.('verticalPageMargins', newValue, verticalPageMargins)
            }}
            unit=" in"
            isIntermediateDotsDisabled
            min={currentTemplate?.settings.verticalPageMargins?.min}
            max={currentTemplate?.settings.verticalPageMargins?.max}
            defaultValue={currentTemplate?.settings.verticalPageMargins?.default}
            step={currentTemplate?.settings.verticalPageMargins?.step}
          />
        </SliderContainer>
        <SliderContainer>
          <SliderLabel>{i18n.t(`${TRANSLATION}.horizontal_page_margins`)}</SliderLabel>
          <GenericAbsoluteSlider
            value={horizontalPageMargins}
            disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
            isDisabled={
              !currentTemplate?.supportsHorizontalPageMargins || !handleSettingsFieldChange
            }
            onChange={newValue => {
              handleSettingsFieldChange?.('horizontalPageMargins', newValue, horizontalPageMargins)
            }}
            unit=" in"
            isIntermediateDotsDisabled
            min={currentTemplate?.settings.horizontalPageMargins?.min}
            max={currentTemplate?.settings.horizontalPageMargins?.max}
            defaultValue={currentTemplate?.settings.horizontalPageMargins?.default}
            step={currentTemplate?.settings.horizontalPageMargins?.step}
          />
        </SliderContainer>
        <SliderContainer>
          <SliderLabel>{i18n.t(`${TRANSLATION}.margins_between_sections`)}</SliderLabel>
          <GenericAbsoluteSlider
            isIntermediateDotsDisabled
            unit=" pt"
            value={marginsBetweenSections}
            disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
            isDisabled={
              !currentTemplate?.supportsMarginsBetweenSections || !handleSettingsFieldChange
            }
            onChange={newValue => {
              handleSettingsFieldChange?.(
                'marginsBetweenSections',
                newValue,
                marginsBetweenSections,
              )
            }}
            min={currentTemplate?.settings.marginsBetweenSections?.min}
            max={currentTemplate?.settings.marginsBetweenSections?.max}
            defaultValue={currentTemplate?.settings.marginsBetweenSections?.default}
            step={currentTemplate?.settings.marginsBetweenSections?.step}
          />
        </SliderContainer>

        <SliderContainer>
          <SliderLabel>
            {i18n.t(`${TRANSLATION}.margins_between_section_name_and_content`)}
          </SliderLabel>
          <GenericAbsoluteSlider
            isIntermediateDotsDisabled
            unit=" pt"
            value={marginsBetweenSectionNameAndContent}
            disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
            isDisabled={
              !currentTemplate?.supportsMarginsBetweenSectionNameAndContent ||
              !handleSettingsFieldChange
            }
            onChange={newValue => {
              handleSettingsFieldChange?.(
                'marginsBetweenSectionNameAndContent',
                newValue,
                marginsBetweenSectionNameAndContent,
              )
            }}
            min={currentTemplate?.settings.marginsBetweenSectionNameAndContent?.min}
            max={currentTemplate?.settings.marginsBetweenSectionNameAndContent?.max}
            defaultValue={currentTemplate?.settings.marginsBetweenSectionNameAndContent?.default}
            step={currentTemplate?.settings.marginsBetweenSectionNameAndContent?.step}
          />
        </SliderContainer>

        <SliderContainer>
          <SliderLabel>{i18n.t(`${TRANSLATION}.margins_between_cards`)}</SliderLabel>
          <GenericAbsoluteSlider
            isIntermediateDotsDisabled
            unit=" pt"
            value={marginsBetweenCards}
            disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
            isDisabled={!currentTemplate?.supportsMarginsBetweenCards || !handleSettingsFieldChange}
            onChange={newValue => {
              handleSettingsFieldChange?.('marginsBetweenCards', newValue, marginsBetweenCards)
            }}
            min={currentTemplate?.settings.marginsBetweenCards?.min}
            max={currentTemplate?.settings.marginsBetweenCards?.max}
            defaultValue={currentTemplate?.settings.marginsBetweenCards?.default}
            step={currentTemplate?.settings.marginsBetweenCards?.step}
          />
        </SliderContainer>

        <TooltipV2
          value={i18n.t(`${TRANSLATION}.date_format_warning`)}
          width={220}
          multiline={true}
          position="bottom"
          isActive={dateFormat.isDisabled}
        >
          <SelectContainer>
            <SelectLabel>{i18n.t(`${TRANSLATION}.date_format`)}</SelectLabel>
            <CustomSelect
              selected={dateFormat.value}
              onSelect={dateFormat.onSelect}
              options={dateFormat.options}
              disabled={dateFormat.isDisabled}
              useNativeSelectOnMobile={false}
            />
          </SelectContainer>
        </TooltipV2>
      </GroupControl>

      <GroupControl title={i18n.t(`${TRANSLATION}.header_alignment`)}>
        <HeaderAlignmentSelection resume={resume} onFieldChange={handleSettingsFieldChange} />
      </GroupControl>

      <GroupControl title={i18n.t(`${TRANSLATION}.date_alignment`)}>
        <DateAlignmentSelection resume={resume} onFieldChange={handleSettingsFieldChange} />
      </GroupControl>

      <GroupControl title={i18n.t(`${TRANSLATION}.location_alignment`)}>
        <LocationAlignmentSelection resume={resume} onFieldChange={handleSettingsFieldChange} />
      </GroupControl>

      <GroupControl title={i18n.t(`${TRANSLATION}.skills_layout`)}>
        <SkillBlockLayoutSelection resume={resume} onFieldChange={handleSettingsFieldChange} />
      </GroupControl>

      <GroupControl title={i18n.t(`${TRANSLATION}.education_block_order`)}>
        <EducationBlockOrderSelection resume={resume} onFieldChange={handleSettingsFieldChange} />
      </GroupControl>

      <GroupControl title={i18n.t(`${TRANSLATION}.education_block_layout`)}>
        <EducationBlockLayoutSelection resume={resume} onFieldChange={handleSettingsFieldChange} />
      </GroupControl>
    </TabContentContainer>
  )
}
